import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export function Stars({ value }) {
  if (value === null || value === undefined) return <div className="cv-stat-sub" style={{ marginTop: 12 }}>Not rated</div>;
  return (
    <div className="cv-stars" aria-label={`${value} out of 5 stars`}>
      {'★'.repeat(value)}<i>{'★'.repeat(5 - value)}</i>
    </div>
  );
}

function Recalls({ recalls }) {
  const [all, setAll] = useState(false);
  const shown = all ? recalls : recalls.slice(0, 4);
  return (
    <div className="cv-panel">
      <h3>Recall campaigns for this model year</h3>
      <p className="cv-panel-sub">Newest first. Many affect only some vehicles — the VIN lookup below tells you whether this exact car is covered and still unrepaired.</p>
      {recalls.length === 0 && <p>NHTSA lists no recall campaigns for this model year.</p>}
      {shown.map((r) => (
        <details className="cv-recall" key={r.id}>
          <summary>
            <span>
              <span className="cv-recall-name">{r.component}</span>{' '}
              {r.doNotDrive && <span className="cv-badge">Do not drive</span>}
            </span>
            <span className="cv-recall-meta">{r.id} · {r.date}</span>
          </summary>
          <div className="cv-recall-body">
            {r.consequence && <p><b>Risk:</b> {r.consequence}</p>}
            {r.remedy && <p><b>Fix:</b> {r.remedy}</p>}
          </div>
        </details>
      ))}
      {recalls.length > 4 && (
        <button className="cv-link-btn" style={{ marginTop: 10 }} onClick={() => setAll((a) => !a)}>
          {all ? 'Show fewer' : `Show all ${recalls.length} recalls`}
        </button>
      )}
    </div>
  );
}

export default function VehicleReport({ data }) {
  const { complaints: c, safety: s, recalls } = data;
  const doNotDrive = recalls.filter((r) => r.doNotDrive).length;
  const max = c && c.top.length ? c.top[0][1] : 1;

  return (
    <section className="cv-report" id="result">
      <div className="cv-wrap">
        <div className="cv-eyebrow">Your check</div>
        <h2 className="cv-report-title" style={{ marginTop: 6 }}>{data.title}</h2>
        <p className="cv-report-sub">
          {[data.trim, data.plant && `Built in ${data.plant}`].filter(Boolean).join(' · ') || 'Model-year data from NHTSA'}
          {data.vin && <span className="mono"> · {data.vin}</span>}
        </p>
        {data.checkDigitWarning && (
          <div className="cv-note"><b>Check the VIN.</b> Its check digit doesn't compute, which usually means a typo. The figures below are for the model the VIN appears to decode to.</div>
        )}

        <div className="cv-stats">
          <div className="cv-stat">
            <div className="cv-stat-label">Recall campaigns</div>
            <div className="cv-stat-value">{recalls.length}</div>
            <div className="cv-stat-sub">
              {doNotDrive > 0 ? <><span className="cv-badge">{doNotDrive} say don't drive</span></> : 'for this model year'}
            </div>
          </div>
          <div className="cv-stat">
            <div className="cv-stat-label">Owner complaints</div>
            <div className="cv-stat-value">{c ? c.count.toLocaleString('en-US') : '—'}</div>
            <div className="cv-stat-sub">
              {c ? (c.crash || c.fire || c.injuries ? `${c.crash} crashes · ${c.fire} fires · ${c.injuries} injuries reported` : 'no crashes, fires or injuries reported') : 'unavailable right now'}
            </div>
          </div>
          <div className="cv-stat">
            <div className="cv-stat-label">NHTSA crash rating</div>
            {s ? <><div className="cv-stat-value">{s.overall}<span style={{ fontSize: 22, color: 'var(--muted)' }}>/5</span></div><Stars value={s.overall} /></> : <><div className="cv-stat-value">—</div><Stars value={null} /></>}
          </div>
        </div>

        <Recalls recalls={recalls} />

        {c && c.top.length > 0 && (
          <div className="cv-panel">
            <h3>What owners complain about most</h3>
            <p className="cv-panel-sub">Complaints filed with NHTSA for this model year, by component.</p>
            <div className="cv-bars">
              {c.top.map(([name, n]) => (
                <div className="cv-bar-row" key={name}>
                  <span>{name}</span>
                  <span className="cv-bar-track"><span className="cv-bar-fill" style={{ display: 'block', width: `${Math.max(4, (n / max) * 100)}%` }} /></span>
                  <span className="cv-bar-n">{n}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {c && c.top.length > 0 && (
          <div className="cv-panel">
            <h3>Have a mechanic look at</h3>
            <p className="cv-panel-sub">A pre-purchase inspection checklist built from the most-reported problem areas.</p>
            <ul className="cv-checklist">
              {c.top.slice(0, 3).map(([name]) => <li key={name}>{name}</li>)}
              {recalls.length > 0 && <li>Proof that recall repairs were done (ask for the dealer service records)</li>}
            </ul>
          </div>
        )}

        {data.specs && data.specs.length > 0 && (
          <details className="cv-panel">
            <summary style={{ cursor: 'pointer', fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 22, fontWeight: 700 }}>Specs decoded from the VIN</summary>
            <div className="cv-specs" style={{ marginTop: 14 }}>
              {data.specs.map(([label, value]) => (
                <div className="cv-spec" key={label}><span>{label}</span><span>{value}</span></div>
              ))}
            </div>
          </details>
        )}

        <div className="cv-panel" style={{ background: 'var(--soft)' }}>
          <h3>Does this exact car still have an open recall?</h3>
          <p style={{ marginTop: 6, color: 'var(--ink-2)' }}>The figures above describe the model year. For one specific car, enter its VIN on NHTSA's official lookup — it's free and takes seconds.</p>
          <p style={{ marginTop: 14 }}>
            <a className="cv-btn cv-btn--ghost cv-btn--sm" href="https://www.nhtsa.gov/recalls" target="_blank" rel="noopener noreferrer">Open NHTSA's VIN recall lookup →</a>
          </p>
        </div>

        <div className="cv-next">
          <div>
            <h3>What this free check can't show</h3>
            <p>Accidents, title brands, odometer readings, ownership count and service history only appear in a paid history report. Not sure which one? Answer five questions.</p>
          </div>
          <Link className="cv-btn" to="/#quiz">Find the right report →</Link>
        </div>
      </div>
    </section>
  );
}
